import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Initializer } from 'common/types/initializers';

import Wahanda from 'common/wahanda';
import { CancellationFlowAnalytics } from 'common/analytics';
import { BOOKING_ACTOR } from 'common/consts';
import App from 'common/backbone-app';
import { useOrderInfoQuery } from 'common/api-hooks/useOrderInfoQuery';
import { usePosStatusQuery } from 'common/api-hooks/usePosStatusQuery';
import { RescheduleOrCancelChoice } from './RescheduleOrCancelChoice';
import { isPrepaidCancellationBlocked } from './prepaidCancellationRules';
import { CancelThroughSupport } from './CancelThroughSupport';
import { CancellationReason } from './CancellationReason';
import { ConfirmCancellationWithBulletPoints } from './ConfirmCancellationWithBulletPoints';
import { ConfirmCancellationWithRefundOptions } from './ConfirmCancellationWithRefundOptions';
import { ConfirmCancellationWithPaymentProtection } from './ConfirmCancellationWithPaymentProtection';
import { usePaymentProtectionQuery } from '../PaymentProtection/usePaymentProtectionQuery';
import { OrderCancellationOptions } from './OrderCancellationOptions';
import {
  CancellationInitializer,
  FinalCancellationProps,
  MultiBookingCancellationProps,
  RescheduleOrCancelProps,
} from './initializer';
import { CancellationConfirmationModalInitializer } from './CancellationConfirmationModal/initializer';

const LANG = Wahanda.lang.calendar.appointments.cancellation;
const CANCELLATION_REASON_TYPE_VENUE = 'venue';
const CANCELLATION_REASON_TYPE_CUSTOMER = 'customer';

type Consumer = {
  emailAddress: string | null;
  id: number;
  name: string;
  phone: string | null;
  prepaymentRequired?: boolean;
};

export interface CancellationProps {
  action: string;
  bookingActor: BOOKING_ACTOR;
  cancellationAllowed: boolean;
  closeParentDialog: () => void;
  consumer?: Consumer;
  doClose: () => void;
  granularity: 'appointment' | 'appointment-group';
  hideParentDialog: () => void;
  id: number;
  isFirstTimeCustomer: boolean;
  isPrepaid: boolean;
  isRecurring: boolean;
  isUnconfirmed: boolean;
  isWithinCancellationPeriod: boolean;
  appointmentModel: {
    collection: {
      models: any[];
      where: (attributes: Record<string, unknown>) => any[];
    };
    attributes: {
      id: number;
      appointmentDate: string;
      appointmentGroupIds: number[];
      bookingActor: 'CUSTOMER' | 'SUPPLIER' | 'SUPPLIERS_CUSTOMER';
      startTime: string | null;
      endTime: string | null;
      offerName: string;
      employeeName: string;
      amount: number;
      payAtVenue: boolean;
      hasEvoucher: boolean;
      notes: string;
      skus: Array<{ skuId: number; skuName: string; nameInherited: boolean }>;
      treatwellFeePercentage: number;
      firstTimeCustomer: boolean;
      walkIn: boolean;
      consumerName: string;
      consumerPhone: string | null;
      consumerEmail: string | null;
      venueCustomerId?: number;
    };
    groups: {
      [key: number]: {
        data: {};
        id: number;
        length: number;
        models: any[];
      };
    };
  };
  onClose: () => void;
  onDoCancellation: (_: any) => void;
  onReschedule: () => void;
  orderId?: number;
  paymentProtected: boolean;
  reschedulingAllowed: boolean;
  // Closes the cancellation flow and shows the appointment editor again
  returnToParentDialog: () => void;
}

enum Step {
  Init,
  CheckOrder,
  CancelOrCall,
  Confirm,
}

export const Cancellation = (props: CancellationProps) => {
  const {
    id,
    granularity,
    orderId,
    paymentProtected,
    closeParentDialog,
    hideParentDialog,
    onReschedule,
    appointmentModel,
    returnToParentDialog,
  } = props;

  const [step, setStep] = useState(Step.Init);
  const [isOrderQueryEnabled, setIsOrderQueryEnabled] = useState(!!orderId);
  const [cancellationReasonType, setCancellationReasonType] = useState<'venue' | 'customer'>();
  const reconnectRef = useRef<Initializer | null>(null);
  const skipClosingParentModal = useRef<boolean>(false);
  const isCancelled = useRef<boolean>(false);

  const { isLoading: isOrderQueryLoading, response: orderInfo } = useOrderInfoQuery(
    orderId,
    isOrderQueryEnabled,
  );

  const { lastClosedDay } = usePosStatusQuery(props.isPrepaid);
  const prepaidCancellationBlocked = isPrepaidCancellationBlocked({
    isPrepaid: props.isPrepaid,
    appointmentDate: appointmentModel?.attributes?.appointmentDate,
    lastClosedDay,
    today: Wahanda.Date.toApiString(Wahanda.Date.createVenueDate()),
  });

  useEffect(() => {
    if (orderId) {
      setIsOrderQueryEnabled(true);
    } else {
      setIsOrderQueryEnabled(false);
    }
  }, [orderId]);

  const handleClose = useCallback(() => {
    reconnectRef.current?.destroy();
    reconnectRef.current = null;
    if (!skipClosingParentModal.current) {
      closeParentDialog();
    }
    skipClosingParentModal.current = false;
  }, [closeParentDialog]);

  // Closing a cancellation modal (X, Esc) goes back to the appointment editor,
  // unless the appointment has just been cancelled
  const handleDismiss = useCallback(() => {
    if (isCancelled.current) {
      handleClose();
      return;
    }
    reconnectRef.current?.destroy();
    reconnectRef.current = null;
    skipClosingParentModal.current = true;
    returnToParentDialog();
  }, [handleClose, returnToParentDialog]);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, [handleClose]);

  const { isLoading, response } = usePaymentProtectionQuery(
    granularity,
    id,
    cancellationReasonType === CANCELLATION_REASON_TYPE_CUSTOMER,
    'cancellation',
  );

  const isOrderWithSingleAppointment = () => {
    const appointments = orderInfo?.appointments || [];
    const appointmentGroups = orderInfo?.appointmentGroups || [];
    return appointments.length + appointmentGroups.length === 1;
  };

  // Deprecated: Who is cancelling is not relevant in the new flow
  const isVenueCancellation = () => cancellationReasonType === CANCELLATION_REASON_TYPE_VENUE;

  // Deprecated: Who is cancelling is not relevant in the new flow
  const isCustomerCancellation = () => cancellationReasonType === CANCELLATION_REASON_TYPE_CUSTOMER;

  const renderPrepayRequirementDialog = () => {
    const { consumer } = props;
    // After cancelling an appointment and specifying that it was initiated by Customer
    // render prepay requirement dialog
    if (isCustomerCancellation() && App.config.canShowPrepayRequirementFeatures()) {
      App.ES6.Initializers.PrepayRequireDialog({
        customerId: consumer?.id,
      }).render();
    }
  };

  const renderConfirmCancellationWithRefundOptions = () => {
    const { bookingActor, isPrepaid, onDoCancellation, onClose } = props;

    const onCancel = (opts) => {
      onDoCancellation({
        ...opts,
        isVenueCancellation: isVenueCancellation(),
      });
      onClose();
      CancellationFlowAnalytics.trackCancellationSuccessful();
      renderPrepayRequirementDialog();
    };

    return (
      <ConfirmCancellationWithRefundOptions
        language={LANG.prepaidWidget.confirmCancellationWithRefundOptions}
        bookingActor={bookingActor}
        isPrepaid={isPrepaid}
        onClose={onClose}
        onCancel={onCancel}
      />
    );
  };

  const renderConfirmCancellationWithPaymentProtection = () => {
    const { bookingActor, onDoCancellation, onClose, isFirstTimeCustomer } = props;

    const onCancel = (opts) => {
      onDoCancellation({
        ...opts,
        isVenueCancellation: isVenueCancellation(),
      });
      onClose();
      CancellationFlowAnalytics.trackCancellationSuccessful();
      renderPrepayRequirementDialog();
    };

    return (
      <ConfirmCancellationWithPaymentProtection
        bookingActor={bookingActor}
        isFirstTimeCustomer={isFirstTimeCustomer}
        onClose={onClose}
        onCancel={onCancel}
      />
    );
  };

  const handleRescheduleWithBackbone = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { consumer } = props;
    const maybeGetEventPosition = () => {
      return event ? { x: event.clientX, y: event.clientY } : null;
    };

    if (window.Wahanda?.Appointment?.enterReschedulingMode) {
      const dayCollection = new App.Collections.CustomerAppointments();
      dayCollection.date = appointmentModel.attributes.appointmentDate;
      dayCollection.customerId = consumer?.id;
      dayCollection.utmSource = `calendar-reschedule`;
      dayCollection.add(appointmentModel);

      // Only include appointments belonging to the same customer's booking on this day,
      // not every appointment currently loaded in the shared calendar collection
      // (which would reschedule the whole employee's day, see THY-2378). Appointments
      // are grouped by customer + day here (not order) since "reschedule all" is meant
      // to move every appointment for this customer that day, even across orders.
      // The id is read off the appointment itself (not the inline client form's
      // `consumer`, which is unpopulated unless that form was filled in for this
      // appointment) so walk-ins without a customer id are never grouped together.
      const venueCustomerId = appointmentModel?.attributes?.venueCustomerId;
      const sameCustomerAppointments =
        venueCustomerId && appointmentModel?.collection?.where
          ? appointmentModel.collection.where({
              venueCustomerId,
              appointmentDate: appointmentModel.attributes.appointmentDate,
            })
          : [];
      for (const model of sameCustomerAppointments) {
        dayCollection.add(model);
      }

      const appointmentGroupId = appointmentModel?.attributes?.appointmentGroupIds?.[0];
      const appointmentGroup = appointmentGroupId
        ? appointmentModel?.groups?.[appointmentGroupId]
        : null;
      const appointmentsToReschedule = appointmentGroup ? [appointmentGroup] : [appointmentModel];

      if (appointmentsToReschedule.length > 0) {
        window.Wahanda.Appointment.enterReschedulingMode(
          appointmentsToReschedule,
          dayCollection,
          maybeGetEventPosition(),
          true,
        );
        closeParentDialog();
      }
    } else {
      if (onReschedule) {
        onReschedule();
      } else {
        console.error('No rescheduling mechanism available');
      }
    }
  };

  const renderCancelOrCall = () => {
    const { onClose, cancellationAllowed, bookingActor } = props;
    const isWidgetOrder = bookingActor === BOOKING_ACTOR.WIDGET;
    const isMarketplaceOrder = bookingActor === BOOKING_ACTOR.CUSTOMER;

    if (
      (bookingActor === BOOKING_ACTOR.CUSTOMER && !cancellationAllowed) ||
      prepaidCancellationBlocked
    ) {
      return <CancelThroughSupport onClose={onClose} />;
    }

    const nextStepWithReason = (type) => () => {
      setCancellationReasonType(type);
      setStep(Step.Confirm);
    };

    if (isWidgetOrder) {
      nextStepWithReason(CANCELLATION_REASON_TYPE_VENUE)();
    }

    if (isMarketplaceOrder) {
      setStep(Step.Confirm);
    }

    return (
      <CancellationReason
        lang={LANG.marketplace.cancellationReason}
        onVenueCancelled={nextStepWithReason(CANCELLATION_REASON_TYPE_VENUE)}
        onCustomerCancelled={nextStepWithReason(CANCELLATION_REASON_TYPE_CUSTOMER)}
        onClose={onClose}
      />
    );
  };

  const renderMultiOrderCancellation = () => {
    const { bookingActor, cancellationAllowed, id: appointmentId, isPrepaid, orderId } = props;

    const closePropmtDialog = () => {
      // keep parent opened when going back from simple dialog prompt
      skipClosingParentModal.current = true;
      // only destroys cancellation component, keeping the parent appointment modal open and usable
      returnToParentDialog();
    };

    const openSupport = () => {
      App.trigger(Wahanda.Event.SALESFORCE_BOT_OPEN, { source: 'cancel_appointment_modal' });
      handleClose();
    };

    const renderCancellationUnavailableModal = () => {
      if (reconnectRef.current !== null) {
        return;
      }

      reconnectRef.current = CancellationInitializer({
        step: 'cancellationUnavailable',
        appointmentId,
        bookingActor,
        isPrepaid,
        orderId,
        onClose: closePropmtDialog,
        onOpenSupport: openSupport,
      });
      reconnectRef.current.render();
    };

    const multiBookingCancellationWarningProps: MultiBookingCancellationProps = {
      step: 'multiAppointmentPrompt',
      appointmentId,
      bookingActor,
      isPrepaid,
      orderId,
      onClose: closePropmtDialog,
      onOpenSupport: openSupport,
      onCancel: () => {
        reconnectRef.current?.destroy();
        reconnectRef.current = null;

        if (cancellationAllowed && !prepaidCancellationBlocked) {
          renderNewCancellationConfirmationModal();
          return;
        }

        renderCancellationUnavailableModal();
      },
    };

    if (reconnectRef.current !== null) {
      return null;
    }

    reconnectRef.current = CancellationInitializer(multiBookingCancellationWarningProps);
    reconnectRef.current.render();
    return null;
  };

  const renderSuggestReschedulingOrCancelling = ({
    language,
    onCancel,
    isMarketplaceCancellationFlow,
  }) => {
    const { onClose, reschedulingAllowed, action, isFirstTimeCustomer, hideParentDialog } = props;

    if (isMarketplaceCancellationFlow) {
      hideParentDialog();

      const rescheduleOrCancelProps: RescheduleOrCancelProps = {
        step: 'rescheduleOrCancel',
        orderId: orderId ?? 0,
        isFirstTimeCustomer,
        reschedulingAllowed,
        onClose: handleDismiss,
        onReschedule: handleRescheduleWithBackbone,
        onCancel,
      };

      if (reconnectRef.current === null) {
        reconnectRef.current = CancellationInitializer(rescheduleOrCancelProps);
        reconnectRef.current.render();
      }

      return null;
    }

    return (
      <RescheduleOrCancelChoice
        lang={language}
        onCancel={onCancel}
        reschedulingAllowed={reschedulingAllowed}
        action={action}
        onReschedule={onReschedule}
        onClose={onClose}
      />
    );
  };

  const renderConfirmMarketplaceCancellation = () => {
    const { isFirstTimeCustomer, bookingActor, isPrepaid, consumer, reschedulingAllowed } = props;

    const handleCancellation = () => {
      const { onDoCancellation, isRecurring } = props;
      const includeFutureRecurrences = isRecurring ? true : undefined;

      if (isRecurring) {
        CancellationFlowAnalytics.trackSubmitCancelFutureAppointments(
          includeFutureRecurrences || false,
        );
      }
      // isVenueCancellaton: true param is needed to correctly process refund on the BE
      onDoCancellation({
        includeFutureRecurrences,
        isVenueCancellation: true,
      });
      handleClose();
    };

    const finalCancellationProps: FinalCancellationProps = {
      step: 'finalConfirmation',
      orderId: orderId ?? 0,
      bookingActor,
      paymentType: isPrepaid ? 'PREPAID' : 'PAY_AT_VENUE',
      isFirstTimeCustomer,
      isPaymentProtected: paymentProtected,
      customer: consumer,
      reschedulingAllowed,
      onClose: handleDismiss,
      onReschedule: handleRescheduleWithBackbone,
      onCancel: handleCancellation,
    };

    if (reconnectRef.current === null) {
      reconnectRef.current = CancellationInitializer(finalCancellationProps);
      reconnectRef.current.render();
    } else {
      reconnectRef.current.destroy();
      reconnectRef.current = CancellationInitializer(finalCancellationProps);
      reconnectRef.current.render();
    }
    return null;
  };

  const renderNewCancellationConfirmationModal = () => {
    hideParentDialog();

    const onCancel = () => {
      isCancelled.current = true;
      App.trigger(Wahanda.Event.CALENDAR_UPDATE_ON_CHANGE);
    };

    const onUnmount = () => {
      handleClose();
    };

    if (reconnectRef.current === null) {
      reconnectRef.current = CancellationConfirmationModalInitializer({
        appointmentId: id,
        granularity,
        onClose: handleDismiss,
        onCancel,
        onReschedule: handleRescheduleWithBackbone,
        onUnmount,
      });
      reconnectRef.current.render();
    }

    return null;
  };

  const renderConfirmCancellationWithBulletPoints = () => {
    const {
      bookingActor,
      isPrepaid,
      onClose,
      onDoCancellation,
      doClose,
      isFirstTimeCustomer,
      isRecurring,
    } = props;

    const onCancel = ({ includeFutureRecurrences }) => {
      if (isRecurring) {
        CancellationFlowAnalytics.trackSubmitCancelFutureAppointments(includeFutureRecurrences);
      }

      onDoCancellation({
        isVenueCancellation: isVenueCancellation(),
        includeFutureRecurrences,
      });
      doClose();
      CancellationFlowAnalytics.trackCancellationSuccessful();
      renderPrepayRequirementDialog();
    };

    return (
      <ConfirmCancellationWithBulletPoints
        bookingActor={bookingActor}
        isPrepaid={isPrepaid}
        onClose={onClose}
        onCancel={onCancel}
        isReasonVenue={isVenueCancellation()}
        isFirstTimeCustomer={isFirstTimeCustomer}
        isRecurring={isRecurring}
      />
    );
  };

  const renderContactSupportModal = () => {
    const { bookingActor, id: appointmentId, isPrepaid, orderId } = props;

    hideParentDialog();

    const openSupport = () => {
      App.trigger(Wahanda.Event.SALESFORCE_BOT_OPEN, { source: 'cancel_appointment_modal' });
      handleClose();
    };

    if (reconnectRef.current === null) {
      reconnectRef.current = CancellationInitializer({
        step: 'cancellationUnavailable',
        appointmentId,
        bookingActor,
        isPrepaid,
        orderId,
        onClose: handleDismiss,
        onOpenSupport: openSupport,
      });
      reconnectRef.current.render();
    }

    return null;
  };

  const handleNewCancellationFlow = () => {
    const { bookingActor, cancellationAllowed, isUnconfirmed } = props;

    const isVenueOrder = bookingActor === BOOKING_ACTOR.SUPPLIER;
    const isMarketplaceOrder = bookingActor === BOOKING_ACTOR.CUSTOMER;

    if (isVenueOrder) {
      return renderNewCancellationConfirmationModal();
    }

    if (
      (isMarketplaceOrder && (!cancellationAllowed || isUnconfirmed)) ||
      prepaidCancellationBlocked
    ) {
      return renderContactSupportModal();
    }

    if (!orderInfo) {
      return null;
    }

    if (!isOrderWithSingleAppointment()) {
      return renderMultiOrderCancellation();
    }

    return renderNewCancellationConfirmationModal();
  };

  const getStepComponent = () => {
    const {
      bookingActor,
      isWithinCancellationPeriod,
      isPrepaid,
      paymentProtected,
      onClose,
    } = props;
    const isLateCustomerLedCancellation = isCustomerCancellation() && !isWithinCancellationPeriod;
    const isMarketplaceOrder = bookingActor === BOOKING_ACTOR.CUSTOMER;
    const isVenueOrder = bookingActor === BOOKING_ACTOR.SUPPLIER;

    const isMarketplaceCancellationFlow = isMarketplaceOrder;
    const newCancellationFlow = Wahanda.Features.isEnabled('THY-1722-new-cancelation-flow');

    switch (step) {
      case Step.Init:
        if (newCancellationFlow) {
          return handleNewCancellationFlow();
        }
        if (isVenueOrder) {
          return renderConfirmCancellationWithBulletPoints();
        }

        return renderSuggestReschedulingOrCancelling({
          language: LANG.marketplace.rescheduleDialog,
          onCancel: () => setStep(Step.CheckOrder),
          isMarketplaceCancellationFlow,
        });
      case Step.CheckOrder:
        // All logic in this case will be redundant with backend support for partial order cancellations
        if (!orderId) {
          setStep(Step.CancelOrCall);
          return null;
        }

        if (!orderInfo) {
          return null;
        }

        if (isOrderWithSingleAppointment() && !isMarketplaceCancellationFlow) {
          setStep(Step.CancelOrCall);
          return null;
        }

        if (isOrderWithSingleAppointment() && isMarketplaceCancellationFlow) {
          setStep(Step.Confirm);
          return null;
        }

        return (
          <OrderCancellationOptions
            onClose={onClose}
            orderInfo={orderInfo}
            onCancel={() => setStep(Step.CancelOrCall)}
          />
        );

      case Step.CancelOrCall:
        /*
         *  Step 2 is based on whether the cancellation is allowed by the channel feature:
         *
         *  If allowed, then go to next step
         *  If NOT allowed, stop here and show the "call us" dialog
         */
        return renderCancelOrCall();
      case Step.Confirm:
        /*
         *  Step 3 is the final confirmation before cancelling, with cancellation
         *  type based texts.
         */

        if (isLoading || isOrderQueryLoading) {
          return null;
        }

        if (isMarketplaceCancellationFlow) {
          if (prepaidCancellationBlocked) {
            return <CancelThroughSupport onClose={onClose} />;
          }
          return renderConfirmMarketplaceCancellation();
        }

        if (
          paymentProtected &&
          response?.canPreventForCancellation &&
          isLateCustomerLedCancellation
        ) {
          return renderConfirmCancellationWithPaymentProtection();
        }

        if (isPrepaid && isLateCustomerLedCancellation) {
          return renderConfirmCancellationWithRefundOptions();
        }

        return renderConfirmCancellationWithBulletPoints();

      default:
        throw new Error(`Unknown step ${step}`);
    }
  };

  return getStepComponent();
};
